// Shared "go to Hub to manage your plan" redirect — native-aware.
//
// Login.js has its own, separate SSO-specific native/web browser handling
// and is intentionally left alone (it's the canonical reference for that
// pattern, not a dependency of this one). This module exists so any *other*
// place that needs to send the user to Hub's billing UI (Settings.js's
// "Choose a plan" CTA, CallLog.js's transcripts upgrade hint, etc.) shares
// one implementation instead of each duplicating the native/web branch.
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { HUB_UPGRADE_URL } from './creditBalance';

function safePlatform() {
  try {
    return Capacitor.getPlatform() || 'web';
  } catch (_) {
    return 'web';
  }
}

export function isNativePlatform() {
  try {
    if (typeof Capacitor.isNativePlatform === 'function') {
      return !!Capacitor.isNativePlatform();
    }
  } catch (_) { /* fall through */ }
  const p = safePlatform();
  return p === 'ios' || p === 'android';
}

/** Send the user to Hub's real, working plan-management UI (the dashboard's
 * Billing & Plan card) — not Truva's own disconnected Stripe checkout, and
 * not Hub's dead /account route. */
export async function openHubBilling() {
  let url;
  try {
    url = new URL(HUB_UPGRADE_URL);
    url.pathname = '/dashboard';
    url.hash = '#billingForm';
  } catch {
    // HUB_UPGRADE_URL should always be absolute; fall back to the value verbatim.
    window.location.href = HUB_UPGRADE_URL;
    return;
  }
  const href = url.toString();
  if (isNativePlatform()) {
    // Native Capacitor build: a plain window.location redirect would steer the
    // app's own WebView away to Hub, stranding the user outside the app shell.
    // Open in the system browser instead (SFSafariViewController / Chrome
    // Custom Tab) — same pattern as Login.js's SSO flow.
    try {
      await Browser.open({ url: href });
    } catch (e) {
      console.warn('[hubBilling] Browser.open failed, falling back to <a>', e);
      const a = document.createElement('a');
      a.href = href;
      a.target = '_blank';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
    }
  } else {
    window.location.href = href;
  }
}
